import React, { useState } from "react";
import { Download, Eye, ExternalLink, Copy, Check } from "lucide-react";

const ASSETS = [
  {
    id: "fb-cover-mascot-branded",
    name: "Cover · Tigik maskot + branding",
    file: "/marketing/fb-cover-tigik-mascot-branded.jpg",
    desc: "Náš Tigik maskot s OSK 76S orbital hlavicou + prominent „TIGIK by SauberEko®\" branding. Tagline „Komunita zvaračov · TIG · Orbital · Pipefitter\". Ready-to-upload cover obrázok pre sociálne siete.",
    size: "557 KB · 16:9 · ready-to-upload",
    badge: "OFICIÁLNE",
  },
  {
    id: "fb-cover-v3-orbital",
    name: "Cover · Orbital head (reálne, bez iskier)",
    file: "/marketing/fb-cover-tigik-v3-orbital.jpg",
    desc: "Reálna Axxair orbitálna hlavica na nerez DN65 — modrofialový arc cez priezor, ŽIADNE iskry (orbital je v argóne). Ľavá tretina prázdna pre text overlay.",
    size: "647 KB · 16:9 · cinematic",
    badge: "NOVÉ v3",
  },
  {
    id: "fb-cover-v3-mascot",
    name: "Cover · Sauberino realistic mascot",
    file: "/marketing/fb-cover-tigik-v3-mascot.jpg",
    desc: "Realistic Sauberino — drsný roboš zvárač s welding helmet, TIG horákom, koženou zásterou. Gold/amber paleta, dramatic lighting.",
    size: "666 KB · 16:9 · cinematic",
    badge: "NOVÉ v3",
  },
  {
    id: "fb-cover-v3-appshot",
    name: "Cover · Záber z Tigik appky",
    file: "/marketing/fb-cover-tigik-v3-appshot.jpg",
    desc: "Tigik UI na telefóne + tablete (kalkulačka DN65 / 75A / 0.18m/min) + nerez rúra s orbital head v pozadí. Premium SaaS look.",
    size: "507 KB · 16:9 · cinematic",
    badge: "NOVÉ v3",
  },
  {
    id: "fb-cover-v1",
    name: "Cover · Welder + Orbital head (v1 — s iskrami)",
    file: "/marketing/fb-cover-tigik.jpg",
    desc: "Faceless zvárač s orbitálnou hlavicou na nerezovej rúre, sparks, gold/black cinematic. POZN: orbital reálne nefrká iskry — pre realistický záber pozri v3.",
    size: "623 KB · 16:9 horizontal",
  },
  {
    id: "fb-cover-v2",
    name: "Cover · Thumbs-up + Weld pool",
    file: "/marketing/fb-cover-tigik-v2.jpg",
    desc: "Detail welder hand thumbs-up + glowing molten weld pool. Cinematic gold accent, dramatic depth of field.",
    size: "610 KB · 16:9 horizontal",
  },
];

export default function MarketingAssets() {
  const [copied, setCopied] = useState(null);
  const base = typeof window !== "undefined" ? window.location.origin : "";

  const copyUrl = (file) => {
    const url = `${base}${file}`;
    navigator.clipboard?.writeText(url);
    setCopied(file);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div data-testid="marketing-assets" className="space-y-4">
      <div>
        <h2 className="font-display font-black text-2xl mb-2">Marketing & Cover obrázky</h2>
        <p className="text-sm text-zinc-400">
          Vygenerované cover obrázky pre sociálne siete. Klikni „Stiahnuť" alebo „Otvoriť" — uloží ti to obrázok do PC v plnom rozlíšení.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {ASSETS.map((a) => (
          <div key={a.id} className="border border-zinc-800 bg-[var(--wb-surface)] overflow-hidden" data-testid={`asset-${a.id}`}>
            <a href={a.file} target="_blank" rel="noopener noreferrer" className="block group relative">
              <img
                src={a.file}
                alt={a.name}
                loading="lazy"
                className="w-full h-56 object-cover group-hover:opacity-80 transition-opacity"
              />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/60">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500 text-black text-xs font-mono uppercase tracking-widest">
                  <Eye className="w-3.5 h-3.5" /> Otvoriť plné rozlíšenie
                </div>
              </div>
            </a>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="font-display font-bold text-sm">{a.name}</div>
                {a.badge && (
                  <span className={`wb-tag !text-[9px] ${a.badge === "OFICIÁLNE" ? "!text-emerald-400 !border-emerald-600" : "!text-gold-400 !border-gold-500"}`}>{a.badge}</span>
                )}
              </div>
              <div className="text-[10px] font-mono text-zinc-500 mb-2">{a.size}</div>
              <p className="text-xs text-zinc-400 leading-relaxed mb-3">{a.desc}</p>
              <div className="flex items-center gap-2 flex-wrap">
                <a
                  href={a.file}
                  download
                  data-testid={`asset-download-${a.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gold-500 hover:bg-gold-400 text-black text-[11px] font-mono uppercase tracking-wider"
                >
                  <Download className="w-3 h-3" /> Stiahnuť
                </a>
                <a
                  href={a.file}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-700 hover:border-zinc-500 text-zinc-300 text-[11px] font-mono uppercase tracking-wider"
                >
                  <ExternalLink className="w-3 h-3" /> Otvoriť
                </a>
                <button
                  type="button"
                  onClick={() => copyUrl(a.file)}
                  data-testid={`asset-copy-${a.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-700 hover:border-zinc-500 text-zinc-300 text-[11px] font-mono uppercase tracking-wider"
                >
                  {copied === a.file ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                  {copied === a.file ? "Skopírované" : "Kopírovať URL"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="border-l-2 border-blue-600 bg-blue-950/20 p-4 text-xs text-zinc-300 leading-relaxed">
        💡 <strong className="text-blue-300">Tip:</strong> <strong>Cover · Tigik maskot + branding</strong> (úplne hore) je ready-to-upload obrázok s naším maskotom a brandom — netreba ho editovať. Použiteľný ako titulná fotka na sociálnych sieťach.
      </div>
    </div>
  );
}
